"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Users,
  Calendar,
  Edit,
  Eye,
  Copy,
  Trash,
  Wifi,
  Tv,
  Music,
  Mic,
  Camera,
  Gamepad,
  MoreVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Studio {
  id: number;
  name: string;
  capacity?: number | null;
  venue_id: number;
  status: string;
  equipment?: any;
  studio_infos?: Array<{
    lang: string;
    name?: string;
    information: string;
  }>;
  studio_medias?: Array<{
    url: string;
    type: string;
    is_primary: boolean;
  }>;
  _count?: {
    bookings?: number;
    shows?: number;
  };
}

interface StudioCardProps {
  studio: Studio;
  venueId: number;
  onDuplicate?: (studio: Studio) => void;
  onDelete?: (studio: Studio) => void;
  canEdit?: boolean;
  canDuplicate?: boolean;
  canDelete?: boolean;
}

const EQUIPMENT_ICONS: Record<string, any> = {
  wifi: Wifi,
  screen: Tv,
  sound: Music,
  microphone: Mic,
  camera: Camera,
  gaming: Gamepad,
};

export function StudioCard({
  studio,
  venueId,
  onDuplicate,
  onDelete,
  canEdit = true,
  canDuplicate = true,
  canDelete = true,
}: StudioCardProps) {
  const [imageError, setImageError] = useState(false);

  // Get studio name based on language preference
  const getStudioName = () => {
    const defaultLang = "en";

    if (studio.studio_infos && studio.studio_infos.length > 0) {
      // Try to find English info first
      const englishInfo = studio.studio_infos.find(
        (info) => info.lang === defaultLang,
      );
      if (englishInfo && englishInfo.name) {
        return englishInfo.name;
      }

      // Fall back to first available language with a name
      const firstInfoWithName = studio.studio_infos.find((info) => info.name);
      if (firstInfoWithName && firstInfoWithName.name) {
        return firstInfoWithName.name;
      }
    }

    // Fall back to main studio name field
    return studio.name;
  };

  const studioName = getStudioName();

  // Debug logging
  console.log("Studio data:", studio);
  console.log("Studio media:", studio.studio_medias);

  const primaryMedia = studio.studio_medias?.find((m) => m.is_primary);
  const primaryImage = studio.studio_medias?.find(
    (m) => m.is_primary && m.type === "image",
  );
  const primaryVideo = studio.studio_medias?.find(
    (m) => m.is_primary && m.type === "video",
  );

  console.log("Primary media:", primaryMedia);
  console.log("Primary image:", primaryImage);
  console.log("Primary video:", primaryVideo);

  const equipment = studio.equipment || {};
  const equipmentList = Object.entries(equipment)
    .filter(([_, value]) => value)
    .slice(0, 5);

  return (
    <div className="group bg-card border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Studio Media */}
      <div className="relative h-48 bg-muted">
        {primaryImage && !imageError ? (
          <img
            src={primaryImage.url}
            alt={studioName}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : primaryVideo ? (
          <div
            className="relative w-full h-full group cursor-pointer"
            onMouseEnter={() => {
              const video = document.querySelector(
                `[data-video-id="${studio.id}"]`,
              ) as HTMLVideoElement;
              if (video) {
                console.log("Attempting to play video for studio:", studio.id);
                video.currentTime = 0;
                video
                  .play()
                  .then(() => {
                    console.log("Video playing successfully");
                  })
                  .catch((error) => {
                    console.error("Video autoplay failed:", error);
                  });
              }
            }}
            onMouseLeave={() => {
              const video = document.querySelector(
                `[data-video-id="${studio.id}"]`,
              ) as HTMLVideoElement;
              if (video) {
                console.log("Pausing video for studio:", studio.id);
                video.pause();
                video.currentTime = 0;
              }
            }}
          >
            <video
              data-video-id={studio.id}
              src={primaryVideo.url}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="auto"
              onCanPlay={() => console.log("Video can play:", studio.id)}
              onError={(e) => console.error("Video error:", e)}
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-200" />
            {/*  <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              Hover to preview
            </div> */}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <Camera className="h-12 w-12" />
          </div>
        )}

        {/* Status Badge */}
        <span
          className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
            studio.status === "active"
              ? "bg-green-100 text-green-800"
              : studio.status === "maintenance"
                ? "bg-yellow-100 text-yellow-800"
                : studio.status === "inactive"
                  ? "bg-gray-100 text-red-800"
                  : "bg-red-100 text-red-800"
          }`}
        >
          {studio.status === "unknown" ? "inactive" : studio.status}
        </span>

        {/* Action Menu */}
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 bg-white/80 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link
                  href={`/superadmin/venue/${venueId}/studios/${studio.id}`}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Studio
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  href={`/superadmin/venue/${venueId}/studios/${studio.id}/schedule`}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  View Schedule
                </Link>
              </DropdownMenuItem>
              {canDuplicate && <DropdownMenuSeparator />}
              {canDuplicate && (
                <DropdownMenuItem onClick={() => onDuplicate?.(studio)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete?.(studio)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Studio Info */}
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-2">{studioName}</h3>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span>
              {studio.capacity
                ? `${studio.capacity} people`
                : "Capacity not set"}
            </span>
          </div>
        </div>

        {/* Equipment Icons */}
        {equipmentList.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            {equipmentList.map(([key, _]) => {
              const Icon = EQUIPMENT_ICONS[key];
              return Icon ? (
                <div key={key} className="p-1.5 bg-muted rounded" title={key}>
                  <Icon className="h-4 w-4" />
                </div>
              ) : null;
            })}
            {Object.keys(equipment).length > 5 && (
              <span className="text-xs text-muted-foreground">
                +{Object.keys(equipment).length - 5} more
              </span>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            {studio._count?.shows || 0} shows
          </div>
          <div className="text-muted-foreground">
            {studio._count?.bookings || 0} bookings
          </div>
        </div>
      </div>
    </div>
  );
}
